import React, {FC, useContext, useEffect, useState} from "react";
import {Alert, Space, Spin, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../Contexts/AuthContext";
import {authorizationHeaders} from "../../api/authorization";
import {StatusTag} from "../../Components/ui/StatusTag";
import {AdminPage, PageHeader} from "../../Components/ui";

type Release = {
    version: string;
    date: string;
    changesHr: string[];
    changesEn: string[];
};

type AppInfo = {version: string; releases: Release[]};
type AppInfoState =
    | {kind: "loading"}
    | {kind: "ready"; info: AppInfo}
    | {kind: "error"};

const AboutAppPage: FC = () => {
    const {t, i18n} = useTranslation();
    const {auth} = useContext(AuthContext);
    const [state, setState] = useState<AppInfoState>({kind: "loading"});
    const language = i18n.language?.toLowerCase().startsWith("hr") ? "hr" : "en";

    useEffect(() => {
        const abortController = new AbortController();
        void fetch("/api/organizer/app-info", {
            credentials: "include",
            headers: authorizationHeaders(auth.token),
            signal: abortController.signal
        }).then(async response => {
            if (!response.ok) throw new Error("App info unavailable.");
            setState({kind: "ready", info: await response.json() as AppInfo});
        }).catch(error => {
            if (!(error instanceof DOMException && error.name === "AbortError")) {
                setState({kind: "error"});
            }
        });
        return () => abortController.abort();
    }, [auth.token]);

    return (
        <AdminPage>
            <PageHeader title={t("about-app-title", {defaultValue: "About the app"})}/>
            <div className="ati-stack ati-stack--loose ati-stack--narrow">
                <Typography.Paragraph>
                    {t("about-app-description", {
                        defaultValue: "EventFlow helps organizers prepare events, manage participant registrations, and work on site."
                    })}
                </Typography.Paragraph>
                {state.kind === "loading" && <Spin/>}
                {state.kind === "error" && <Alert type="error" showIcon message={t("about-app-error", {
                    defaultValue: "Version information is currently unavailable."
                })}/>}
                {state.kind === "ready" && (
                    <>
                        <Space>
                            <Typography.Text strong>{t("about-app-version", {defaultValue: "Version"})}</Typography.Text>
                            <StatusTag tone="info">{state.info.version}</StatusTag>
                        </Space>
                        <Typography.Title level={3} style={{margin: 0}}>
                            {t("about-app-changelog", {defaultValue: "What's new"})}
                        </Typography.Title>
                        {state.info.releases.map(release => {
                            const changes = language === "hr" ? release.changesHr : release.changesEn;
                            return (
                                <section key={`${release.version}-${release.date}`}>
                                    <Typography.Title level={4}>
                                        {release.version} <Typography.Text type="secondary">{release.date}</Typography.Text>
                                    </Typography.Title>
                                    <ul>
                                        {changes.map((change, index) => <li key={index}>{change}</li>)}
                                    </ul>
                                </section>
                            );
                        })}
                    </>
                )}
            </div>
        </AdminPage>
    );
};

export default AboutAppPage;
